/**
 * Fuzzy column matching utility for intelligent field mapping
 * Matches exporter's column names to our internal field names
 */

import { shipmentsExcelMapping } from './shipments-excel-mapping';

export type FieldMapping = {
  [exporterColumn: string]: string; // maps to internal field name
};

export type MappingSuggestion = {
  exporterColumn: string;
  suggestedField: string | null;
  confidence: 'high' | 'medium' | 'low' | 'none';
  alternatives: Array<{ field: string; score: number }>;
};

// Common variations for each internal field
const FIELD_VARIATIONS: Record<string, string[]> = {
  reference_number: [
    'referencia', 'reference', 'ref', 'numero referencia', 'nro referencia',
    'ref number', 'ref #', 'reference #', 'numero de referencia'
  ],
  container_number: [
    'contenedor', 'container', 'cont', 'container #', 'cont #', 'cnt',
    'numero contenedor', 'nro contenedor', 'container number'
  ],
  receiver: [
    'cliente', 'client', 'receiver', 'destinatario', 'comprador',
    'buyer', 'customer', 'consignee'
  ],
  ship_name: [
    'nave', 'ship', 'vessel', 'barco', 'nombre nave', 'ship name',
    'vessel name', 'buque'
  ],
  ship_type: [
    'tipo nave', 'ship type', 'tipo', 'type', 'vessel type',
    'tipo de nave', 'modo transporte'
  ],
  ship_etd: [
    'etd', 'estimated time departure', 'fecha salida', 'salida',
    'departure', 'fecha embarque'
  ],
  ship_eta: [
    'eta', 'estimated time arrival', 'fecha llegada', 'llegada',
    'arrival', 'fecha arribo'
  ],
  quantity: [
    'cantidad', 'quantity', 'cant', 'qty', 'bultos', 'cajas',
    'boxes', 'numero bultos', 'nro bultos'
  ],
  variety: [
    'variedad', 'variety', 'var', 'especie', 'tipo fruta'
  ],
  caliber: [
    'calibre', 'caliber', 'size', 'cal', 'tamaño', 'medida'
  ],
  color: [
    'color', 'colour', 'tonalidad', 'shade'
  ],
  box_label: [
    'etiqueta', 'label', 'marca', 'brand', 'etiq', 'etiqueta caja'
  ],
  box_format: [
    'formato', 'format', 'tipo caja', 'box format', 'box type'
  ],
  box_weight: [
    'peso caja', 'box weight', 'peso', 'weight', 'kg caja',
    'kilos caja', 'net weight'
  ],
  box_type: [
    'codigo caja', 'box code', 'codigo', 'code', 'tipo bulto',
    'packaging code', 'pack code'
  ],
  box_description: [
    'descripcion', 'description', 'desc', 'detalle', 'detail'
  ],
  category: [
    'categoria', 'category', 'cat', 'clase', 'grado', 'grade',
    'quality', 'calidad'
  ],
  labeled_variety: [
    'variedad etiquetada', 'labeled variety', 'var etiqueta',
    'label variety', 'commercial variety'
  ],
  producer_group: [
    'grupo productor', 'producer group', 'grupo', 'group',
    'grower group', 'cooperative'
  ],
  producer_name: [
    'productor', 'producer', 'nombre productor', 'grower',
    'farmer', 'agricultor', 'proveedor'
  ],
  producer_national_id: [
    'rut productor', 'rut', 'national id', 'tax id', 'dni',
    'id productor', 'producer id', 'grower id'
  ],
  producer_sag_code: [
    'codigo sag', 'sag code', 'sag', 'codigo', 'registration code',
    'farm code'
  ],
  pallet: [
    'pallet', 'tarima', 'palet', 'pallet #', 'numero pallet'
  ],
  reception_date: [
    'fecha recepcion', 'reception date', 'fecha ingreso', 'intake date',
    'receiving date', 'received date'
  ],
  process_date: [
    'fecha proceso', 'process date', 'fecha empaque', 'packing date',
    'production date'
  ],
  packing_name: [
    'planta de proceso', 'packing', 'planta', 'packing house',
    'facility', 'processing plant', 'empaque'
  ],
  lot: [
    'lote', 'lot', 'batch', 'lot number', 'numero lote', 'lote #'
  ],
  process_internal_id: [
    'nro proceso', 'process number', 'numero proceso', 'process id',
    'production number', 'pack number'
  ]
};

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Normalize a string for comparison
 */
function normalize(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9\s]/g, '') // Remove special chars
    .replace(/\s+/g, ' '); // Normalize spaces
}

/**
 * Calculate similarity score between two strings (0-1)
 */
function calculateSimilarity(str1: string, str2: string): number {
  const norm1 = normalize(str1);
  const norm2 = normalize(str2);

  // Exact match - highest priority (e.g., "cliente" === "cliente")
  if (norm1 === norm2) return 1.0;

  // Split into words for word-level matching
  const words1 = norm1.split(' ').filter(w => w.length > 0);
  const words2 = norm2.split(' ').filter(w => w.length > 0);

  // Check if either string is a single word that matches exactly
  if (words1.length === 1 && words2.length === 1) {
    // Both are single words - already checked exact match above
    // Continue to other checks
  } else {
    // Check if any word matches exactly (for multi-word strings)
    for (const w1 of words1) {
      for (const w2 of words2) {
        if (w1 === w2 && w1.length > 3) { // Ignore very short words
          return 0.95; // Very high score for exact word match
        }
      }
    }
  }

  // Contains match (substring) - but heavily penalize if it's ambiguous
  if (norm1.includes(norm2) || norm2.includes(norm1)) {
    const longer = Math.max(norm1.length, norm2.length);
    const shorter = Math.min(norm1.length, norm2.length);
    const ratio = shorter / longer;

    // If the shorter string is the full match, score highly
    if (ratio === 1.0) {
      return 0.98; // Almost perfect
    }

    // If the match is a very small portion, heavily penalize
    if (ratio < 0.6) {
      return 0.4 * ratio; // Very low score for weak partial matches
    }

    return 0.7 + (0.25 * ratio); // 0.7-0.95 range for decent substrings
  }

  // Levenshtein distance for typos and similar strings
  const distance = levenshteinDistance(norm1, norm2);
  const maxLength = Math.max(norm1.length, norm2.length);

  // Only use Levenshtein for strings of similar length (within 30%)
  const lengthDiff = Math.abs(norm1.length - norm2.length);
  const lengthRatio = lengthDiff / maxLength;

  if (lengthRatio > 0.3) {
    // Very different lengths - low score
    return Math.max(0, 0.4 - lengthRatio);
  }

  const similarity = 1 - (distance / maxLength);
  return Math.max(0, similarity * 0.9); // Cap at 0.9 for Levenshtein matches
}

/**
 * Find the best matching internal field for a given exporter column
 */
export function findBestMatch(
  exporterColumn: string,
  internalFields: string[] = Object.values(shipmentsExcelMapping)
): MappingSuggestion {
  const scores: Array<{ field: string; score: number; matchType: string }> = [];
  const normalizedExporterColumn = normalize(exporterColumn);

  for (const internalField of internalFields) {
    let maxScore = 0;
    let bestMatchType = 'fuzzy';

    // Check against the Spanish mapping first
    const spanishNames = Object.entries(shipmentsExcelMapping)
      .filter(([_, value]) => value === internalField)
      .map(([key]) => key);

    for (const spanishName of spanishNames) {
      const normalizedSpanish = normalize(spanishName);

      // Exact match gets perfect score
      if (normalizedExporterColumn === normalizedSpanish) {
        maxScore = 1.0;
        bestMatchType = 'exact-spanish';
        break;
      }

      const score = calculateSimilarity(exporterColumn, spanishName);
      if (score > maxScore) {
        maxScore = score;
        bestMatchType = 'spanish';
      }
    }

    // Only check variations if we don't have a perfect match
    if (maxScore < 1.0) {
      const variations = FIELD_VARIATIONS[internalField] || [];
      for (const variation of variations) {
        const normalizedVariation = normalize(variation);

        // Exact match with variation gets perfect score
        if (normalizedExporterColumn === normalizedVariation) {
          maxScore = 1.0;
          bestMatchType = 'exact-variation';
          break;
        }

        const score = calculateSimilarity(exporterColumn, variation);
        if (score > maxScore) {
          maxScore = score;
          bestMatchType = 'variation';
        }
      }
    }

    scores.push({ field: internalField, score: maxScore, matchType: bestMatchType });
  }

  // Sort by score (exact matches will be 1.0 and come first)
  scores.sort((a, b) => {
    // Prioritize exact matches
    if (a.score === 1.0 && b.score < 1.0) return -1;
    if (b.score === 1.0 && a.score < 1.0) return 1;
    // Then sort by score
    return b.score - a.score;
  });

  const topScore = scores[0];
  const alternatives = scores.slice(1, 4); // Top 3 alternatives

  let confidence: 'high' | 'medium' | 'low' | 'none' = 'none';
  let suggestedField: string | null = null;

  // Exact match = always high confidence
  if (topScore.score === 1.0) {
    confidence = 'high';
    suggestedField = topScore.field;
  } else if (topScore.score >= 0.85) {
    confidence = 'high';
    suggestedField = topScore.field;
  } else if (topScore.score >= 0.65) {
    confidence = 'medium';
    suggestedField = topScore.field;
  } else if (topScore.score >= 0.45) {
    confidence = 'low';
    suggestedField = topScore.field;
  }

  return {
    exporterColumn,
    suggestedField,
    confidence,
    alternatives: alternatives.map(a => ({ field: a.field, score: a.score }))
  };
}

/**
 * Generate mapping suggestions for all exporter columns
 */
export function suggestMappings(exporterColumns: string[]): MappingSuggestion[] {
  return exporterColumns.map(col => findBestMatch(col));
}

/**
 * Convert suggestions to a field mapping object
 */
export function suggestionsToMapping(suggestions: MappingSuggestion[]): FieldMapping {
  const mapping: FieldMapping = {};

  suggestions.forEach(suggestion => {
    if (suggestion.suggestedField) {
      mapping[suggestion.exporterColumn] = suggestion.suggestedField;
    }
  });

  return mapping;
}

/**
 * Check if exporter columns match a saved mapping profile
 * Returns confidence score (0-1)
 *
 * savedMapping is now { system_field: excel_column }
 */
export function matchesProfile(
  exporterColumns: string[],
  savedMapping: FieldMapping
): number {
  // Extract excel columns from the saved mapping (values, not keys)
  const savedExcelColumns = Object.values(savedMapping);

  // Calculate intersection
  const normalizedExporterCols = exporterColumns.map(normalize);
  const normalizedSavedCols = savedExcelColumns.map(normalize);

  const intersection = normalizedExporterCols.filter(col =>
    normalizedSavedCols.includes(col)
  );

  // Match percentage
  const matchPercentage = intersection.length / Math.max(
    exporterColumns.length,
    savedExcelColumns.length
  );

  return matchPercentage;
}

/**
 * Get user-friendly field label from internal field name
 */
export function getFieldLabel(internalField: string): string {
  const spanishLabel = Object.entries(shipmentsExcelMapping)
    .find(([_, value]) => value === internalField)?.[0];

  return spanishLabel || internalField;
}

/**
 * Validate that all required fields are mapped
 */
/**
 * Validate that all required system fields are mapped
 * mapping is now { system_field: excel_column }
 */
export function validateMapping(mapping: FieldMapping): {
  valid: boolean;
  missingFields: string[];
} {
  const requiredFields = [
    'reference_number',
    'quantity',
    'variety',
    'caliber',
    'color',
    'box_format',
    'box_weight',
    'producer_name'
  ];

  // Now we check the keys (system fields) instead of values
  const mappedSystemFields = Object.keys(mapping);
  const missingFields = requiredFields.filter(
    field => !mappedSystemFields.includes(field) || !mapping[field]
  );

  return {
    valid: missingFields.length === 0,
    missingFields
  };
}
